// src/pages/SeoPage.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BlocksRenderer } from '@strapi/blocks-react-renderer';

import Header from '../components/Header';
import Footer from '../components/Footer';
import LoginModal from '../components/LoginModal';
import RegistrationModal from '../components/RegistrationModal';
import InfoModal from '../components/InfoModal';
import { ReactComponent as ChevronUpIcon } from '../assets/icon-chevron-up.svg';
import SEO from '../components/SEO';

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL;

function SeoPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Alleen bij een echte 'bestaat niet' krijgt de pagina noindex. Bij een tijdelijke
  // laadfout niet: Google zou de pagina dan uit de index halen.
  const [notFound, setNotFound] = useState(false);

  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [privacyData, setPrivacyData] = useState(null);
  const [contactData, setContactData] = useState(null);

  useEffect(() => {
    const fetchPageData = async () => {
      setLoading(true);
      setError(null);
      setNotFound(false);
      try {
        const url = `${STRAPI_URL}/api/public-pages?filters[slug][$eq]=${slug}&populate=*`;
        const response = await axios.get(url);

        if (response.data?.data?.length > 0) {
          // Strapi v5 geeft data direct terug (niet in attributes)
          const rawData = response.data.data[0];

          // Check if rawData directly has the fields or if they're nested in attributes
          const pageData = rawData.attributes ? {
            id: rawData.id,
            ...rawData.attributes
          } : rawData;

          setPage(pageData);
        } else {
          setError('Pagina niet gevonden');
          setNotFound(true);
        }
      } catch (err) {
        console.error('Fout bij het ophalen van de pagina:', err);
        setError('Deze pagina laden lukte niet. Probeer het opnieuw.');
      } finally {
        setLoading(false);
      }
    };

    fetchPageData();
  }, [slug]);

  useEffect(() => {
    // Fetch privacy and contact data for footer
    const fetchFooterData = async () => {
      try {
        const [privacyRes, contactRes] = await Promise.all([
          axios.get(`${STRAPI_URL}/api/privacyverklaring?populate=*`),
          axios.get(`${STRAPI_URL}/api/contact-pagina?populate=*`)
        ]);
        setPrivacyData(privacyRes.data.data);
        setContactData(contactRes.data.data);
      } catch (err) {
        console.error('Fout bij het ophalen van footer data:', err);
      }
    };

    fetchFooterData();
  }, []);

  const handleLoginClick = () => {
    setIsLoginModalOpen(true);
  };

  const handleRegisterClick = () => {
    setIsRegisterModalOpen(true);
  };

  if (loading) {
    return (
      <div className="page-container">
        <Header
          onLoginClick={handleLoginClick}
          onRegisterClick={handleRegisterClick}
        />
        <main>
          <p>Pagina wordt geladen...</p>
        </main>
        <Footer
          onLoginClick={handleLoginClick}
          onPrivacyClick={() => setIsPrivacyModalOpen(true)}
          onContactClick={() => setIsContactModalOpen(true)}
        />
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="page-container">
        <SEO
          title="Pagina niet gevonden | Doorloopjes"
          description="Deze pagina bestaat niet of is verplaatst."
          noindex={notFound}
        />
        <Header
          onLoginClick={handleLoginClick}
          onRegisterClick={handleRegisterClick}
        />
        <main>
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <h1>Pagina niet gevonden</h1>
            <p>{error || 'Deze pagina bestaat niet of is verplaatst.'}</p>
          </div>
        </main>
        <Footer
          onLoginClick={handleLoginClick}
          onPrivacyClick={() => setIsPrivacyModalOpen(true)}
          onContactClick={() => setIsContactModalOpen(true)}
        />
      </div>
    );
  }

  // Extract description from first paragraph of body
  const getDescription = () => {
    if (page.body && Array.isArray(page.body) && page.body[0]?.children?.[0]?.text) {
      const text = page.body[0].children[0].text;
      return text.length > 160 ? text.substring(0, 160) + '...' : text;
    }
    return 'Korte doorloopjes voor leraren, direct toepasbaar in de klas.';
  };

  return (
    <div className="page-container seo-page">
      <SEO
        title={`${page.title} | Doorloopjes`}
        description={getDescription()}
        url={`/info/${slug}`}
        type="article"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: page.title,
          description: getDescription(),
          url: `https://doorloopjes.nl/info/${slug}`,
          inLanguage: 'nl',
          isPartOf: {
            '@type': 'WebSite',
            name: 'Doorloopjes',
            url: 'https://doorloopjes.nl',
          },
          publisher: {
            '@type': 'Organization',
            name: 'Doorloopjes',
            url: 'https://doorloopjes.nl',
          },
        }}
      />
      <Header
        onLoginClick={handleLoginClick}
        onRegisterClick={handleRegisterClick}
      />

      {isLoginModalOpen && (
        <LoginModal
          onClose={() => setIsLoginModalOpen(false)}
          onLoginSuccess={() => {
            setIsLoginModalOpen(false);
            window.location.href = '/dashboard';
          }}
          onOpenRegister={() => {
            setIsLoginModalOpen(false);
            setIsRegisterModalOpen(true);
          }}
        />
      )}

      {isRegisterModalOpen && (
        <RegistrationModal
          onClose={() => setIsRegisterModalOpen(false)}
          onRegisterSuccess={() => {
            setIsRegisterModalOpen(false);
            window.location.href = '/dashboard';
          }}
        />
      )}

      <InfoModal
        show={isPrivacyModalOpen}
        onClose={() => setIsPrivacyModalOpen(false)}
        title={privacyData?.title}
        content={privacyData?.body}
        loading={false}
        showSocial={false}
      />

      <InfoModal
        show={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        title={contactData?.title}
        content={contactData?.body}
        loading={false}
        showSocial={true}
      />

      <main>
        <div className="seo-page-content">
          <button className="back-button" onClick={() => navigate('/')}>
            <ChevronUpIcon className="back-icon" />
            <span>Terug</span>
          </button>

          {page.type && (
            <p style={{ fontSize: '0.875rem', color: '#666', marginBottom: '0.5rem', marginTop: '1rem' }}>
              {page.type}
            </p>
          )}

          <h1>{page.title}</h1>

          {page.body && Array.isArray(page.body) && (
            <div style={{ marginTop: '1.5rem' }}>
              <BlocksRenderer content={page.body} />
            </div>
          )}

          <div className="example-cta-section">
            <div className="example-cta-content">
              {slug?.includes('digidate') ? (
                <>
                  <h3>Wil je meer webinars bekijken?</h3>
                  <p>Maak gratis een account. Dan kun je alle webinars, doorloopjes, de assistent en de kennisbank gebruiken.</p>
                </>
              ) : (
                <>
                  <h3>Wil je meer doorloopjes bekijken?</h3>
                  <p>Maak gratis een account. Dan kun je alle doorloopjes, de assistent en de kennisbank gebruiken.</p>
                </>
              )}
              <button onClick={handleRegisterClick} className="btn-primary">
                Gratis registreren
              </button>
            </div>
          </div>
        </div>
      </main>

      <Footer
        onLoginClick={handleLoginClick}
        onPrivacyClick={() => setIsPrivacyModalOpen(true)}
        onContactClick={() => setIsContactModalOpen(true)}
      />
    </div>
  );
}

export default SeoPage;
